import { AIModule } from '../types';

// AI Prototyping & Rapid Development modules
export const STREAMLIT_AI_MODULE: AIModule = {
  id: 'streamlit-ai',
  title: 'Streamlit for AI Applications',
  description: 'Build interactive AI applications rapidly using Streamlit',
  duration: '4 hours',
  difficulty: 'beginner',
  content: `
# Streamlit for AI Applications

## Introduction to Streamlit

Streamlit is a powerful Python framework that transforms data scripts into shareable web applications in minutes. It's particularly well-suited for AI and machine learning applications.

## Core Streamlit Concepts

### App Structure
- Single-file applications
- Automatic rerun on code changes
- State management and caching
- Layout and organization

### Widgets and Interactions
- Input widgets (sliders, text inputs, file uploads)
- Display widgets (charts, tables, media)
- Layout components (columns, containers, sidebars)
- Interactive elements (buttons, forms, callbacks)

### Data Visualization
- Built-in charting capabilities
- Integration with popular libraries
- Real-time data updates
- Interactive visualizations

## Building AI-Powered Streamlit Apps

### Machine Learning Model Integration
- Loading and caching models
- Real-time predictions
- Model comparison interfaces
- Performance monitoring dashboards

### Natural Language Processing Apps
- Text analysis interfaces
- Sentiment analysis tools
- Language translation apps
- Chatbot interfaces

### Computer Vision Applications
- Image upload and processing
- Real-time camera feeds
- Object detection interfaces
- Image generation tools

## Advanced Streamlit Features

### Caching and Performance
- @st.cache_data for data caching
- @st.cache_resource for model caching
- Session state management
- Memory optimization techniques

### Deployment Options
- Streamlit Cloud hosting
- Docker containerization
- Cloud platform deployment
- Custom domain configuration
  `,
  objectives: [
    'Create interactive AI applications with Streamlit',
    'Integrate machine learning models',
    'Build user-friendly interfaces',
    'Deploy applications to production'
  ],
  codeExamples: [
    {
      id: 'streamlit-ml-app',
      title: 'ML Model Interface',
      description: 'Creating an interactive machine learning application',
      code: `
import streamlit as st
import pandas as pd
import numpy as np
from sklearn.ensemble import RandomForestClassifier
from sklearn.datasets import make_classification
import plotly.express as px

# App configuration
st.set_page_config(
    page_title="ML Model Demo",
    page_icon="🤖",
    layout="wide"
)

st.title("🤖 Machine Learning Model Demo")
st.markdown("Interactive interface for model training and prediction")

# Sidebar for model parameters
st.sidebar.header("Model Configuration")
n_samples = st.sidebar.slider("Number of samples", 100, 1000, 500)
n_features = st.sidebar.slider("Number of features", 2, 20, 10)
n_estimators = st.sidebar.slider("Number of trees", 10, 200, 100)

# Cache expensive computations
@st.cache_data
def generate_data(n_samples, n_features):
    """Generate synthetic dataset"""
    X, y = make_classification(
        n_samples=n_samples,
        n_features=n_features,
        n_informative=n_features//2,
        n_redundant=0,
        random_state=42
    )
    return X, y

@st.cache_resource
def train_model(X, y, n_estimators):
    """Train and cache the model"""
    model = RandomForestClassifier(
        n_estimators=n_estimators,
        random_state=42
    )
    model.fit(X, y)
    return model

# Main content
col1, col2 = st.columns(2)

with col1:
    st.subheader("📊 Dataset")
    
    # Generate and display data
    X, y = generate_data(n_samples, n_features)
    df = pd.DataFrame(X[:, :2], columns=['Feature 1', 'Feature 2'])
    df['Target'] = y
    
    # Interactive scatter plot
    fig = px.scatter(
        df, 
        x='Feature 1', 
        y='Feature 2', 
        color='Target',
        title="Dataset Visualization"
    )
    st.plotly_chart(fig, use_container_width=True)
    
    # Data statistics
    st.write("Dataset Statistics:")
    st.write(df.describe())

with col2:
    st.subheader("🎯 Model Performance")
    
    # Train model
    model = train_model(X, y, n_estimators)
    
    # Model accuracy
    accuracy = model.score(X, y)
    st.metric("Training Accuracy", f"{accuracy:.3f}")
    
    # Feature importance
    importance_df = pd.DataFrame({
        'Feature': [f'Feature {i+1}' for i in range(n_features)],
        'Importance': model.feature_importances_
    }).sort_values('Importance', ascending=True)
    
    fig_importance = px.bar(
        importance_df.tail(10),
        x='Importance',
        y='Feature',
        orientation='h',
        title="Top 10 Feature Importance"
    )
    st.plotly_chart(fig_importance, use_container_width=True)

# Prediction interface
st.subheader("🔮 Make Predictions")

# Create input form
with st.form("prediction_form"):
    st.write("Enter feature values for prediction:")
    
    # Dynamic input creation
    inputs = []
    cols = st.columns(min(n_features, 4))
    
    for i in range(n_features):
        col_idx = i % len(cols)
        with cols[col_idx]:
            value = st.number_input(
                f"Feature {i+1}",
                value=0.0,
                key=f"feature_{i}"
            )
            inputs.append(value)
    
    submitted = st.form_submit_button("Predict")
    
    if submitted:
        # Make prediction
        prediction = model.predict([inputs])[0]
        probability = model.predict_proba([inputs])[0]
        
        # Display results
        col1, col2 = st.columns(2)
        
        with col1:
            st.success(f"Prediction: **Class {prediction}**")
        
        with col2:
            st.write("Class Probabilities:")
            prob_df = pd.DataFrame({
                'Class': [f'Class {i}' for i in range(len(probability))],
                'Probability': probability
            })
            st.bar_chart(prob_df.set_index('Class'))

# Model download
st.subheader("💾 Export Model")
if st.button("Download Model"):
    import pickle
    model_bytes = pickle.dumps(model)
    st.download_button(
        label="Download Trained Model",
        data=model_bytes,
        file_name="trained_model.pkl",
        mime="application/octet-stream"
    )
      `,
      language: 'python',
      category: 'tutorial'
    }
  ],
  quiz: {
    questions: [
      {
        id: 'streamlit-caching',
        question: 'Which Streamlit decorator is used for caching machine learning models?',
        options: [
          '@st.cache_data',
          '@st.cache_resource',
          '@st.cache_model',
          '@st.memo'
        ],
        correctAnswer: 1,
        explanation: '@st.cache_resource is used for caching models and other non-serializable objects.'
      }
    ]
  }
};

export const GRADIO_ML_MODULE: AIModule = {
  id: 'gradio-ml',
  title: 'Gradio for ML Model Demos',
  description: 'Create stunning demos for machine learning models using Gradio',
  duration: '3 hours',
  difficulty: 'beginner',
  content: `
# Gradio for ML Model Demos

## What is Gradio?

Gradio is an open-source Python library that allows you to quickly create customizable UI components for your machine learning models, APIs, or any Python function.

## Core Gradio Concepts

### Interface Components
- Input components (text, image, audio, video)
- Output components (text, image, plot, dataframe)
- Interactive elements (sliders, dropdowns, checkboxes)
- Layout options (tabs, columns, rows)

### Interface Creation
- Simple function wrapping
- Multiple input/output handling
- Real-time updates
- Batch processing support

### Sharing and Deployment
- Public link generation
- Hugging Face Spaces integration
- Custom hosting options
- Embedding capabilities

## Building ML Demos with Gradio

### Image Classification Demo
Creating interfaces for computer vision models.

### Text Generation Demo
Building interfaces for language models.

### Audio Processing Demo
Creating interfaces for speech and audio models.

### Multi-Modal Demos
Combining different input/output types.

## Advanced Gradio Features

### Custom Components
- Building reusable components
- Styling and theming
- Custom CSS integration
- JavaScript interactions

### API Integration
- RESTful API creation
- Authentication and security
- Rate limiting
- Error handling

### Performance Optimization
- Model caching strategies
- Async processing
- Queue management
- Resource monitoring
  `,
  objectives: [
    'Build interactive ML model demos',
    'Create multi-modal interfaces',
    'Deploy demos to Hugging Face Spaces',
    'Optimize demo performance'
  ],
  codeExamples: [
    {
      id: 'gradio-image-classifier',
      title: 'Image Classification Demo',
      description: 'Creating an image classification interface with Gradio',
      code: `
import gradio as gr
import torch
import torch.nn.functional as F
from torchvision import transforms
from PIL import Image
import requests
import json

# Load a pre-trained model (example with ResNet)
model = torch.hub.load('pytorch/vision:v0.10.0', 'resnet18', pretrained=True)
model.eval()

# Load ImageNet class labels
url = "https://raw.githubusercontent.com/pytorch/hub/master/imagenet_classes.txt"
classes = requests.get(url).text.strip().split('\\n')

# Image preprocessing
preprocess = transforms.Compose([
    transforms.Resize(256),
    transforms.CenterCrop(224),
    transforms.ToTensor(),
    transforms.Normalize(mean=[0.485, 0.456, 0.406], 
                        std=[0.229, 0.224, 0.225]),
])

def classify_image(image):
    """Classify an uploaded image"""
    if image is None:
        return "Please upload an image"
    
    # Preprocess the image
    input_tensor = preprocess(image)
    input_batch = input_tensor.unsqueeze(0)
    
    # Make prediction
    with torch.no_grad():
        output = model(input_batch)
        probabilities = F.softmax(output[0], dim=0)
    
    # Get top 5 predictions
    top5_prob, top5_catid = torch.topk(probabilities, 5)
    
    results = {}
    for i in range(5):
        class_name = classes[top5_catid[i]]
        probability = top5_prob[i].item()
        results[class_name] = probability
    
    return results

def analyze_confidence(image):
    """Analyze prediction confidence distribution"""
    if image is None:
        return None
    
    # Get all predictions
    input_tensor = preprocess(image)
    input_batch = input_tensor.unsqueeze(0)
    
    with torch.no_grad():
        output = model(input_batch)
        probabilities = F.softmax(output[0], dim=0)
    
    # Create confidence analysis
    max_prob = probabilities.max().item()
    entropy = -(probabilities * torch.log(probabilities + 1e-8)).sum().item()
    
    analysis = {
        "Max Confidence": f"{max_prob:.3f}",
        "Prediction Entropy": f"{entropy:.3f}",
        "Confidence Level": "High" if max_prob > 0.7 else "Medium" if max_prob > 0.3 else "Low"
    }
    
    return analysis

# Create Gradio interface with multiple outputs
with gr.Blocks(theme=gr.themes.Soft()) as demo:
    gr.Markdown("# 🖼️ Image Classification Demo")
    gr.Markdown("Upload an image to see the AI's classification results and confidence analysis.")
    
    with gr.Row():
        with gr.Column(scale=1):
            image_input = gr.Image(
                type="pil", 
                label="Upload Image",
                height=300
            )
            
            classify_btn = gr.Button(
                "🔍 Classify Image", 
                variant="primary",
                size="lg"
            )
            
            gr.Examples(
                examples=[
                    "https://example.com/dog.jpg",
                    "https://example.com/cat.jpg",
                    "https://example.com/car.jpg"
                ],
                inputs=image_input,
                label="Try these examples:"
            )
        
        with gr.Column(scale=1):
            gr.Markdown("### 📊 Classification Results")
            classification_output = gr.Label(
                label="Top 5 Predictions",
                num_top_classes=5
            )
            
            gr.Markdown("### 📈 Confidence Analysis")
            confidence_output = gr.JSON(
                label="Prediction Confidence"
            )
            
            gr.Markdown("### ℹ️ Model Information")
            gr.Markdown("""
            - **Model**: ResNet-18
            - **Dataset**: ImageNet (1000 classes)
            - **Accuracy**: ~70% Top-1, ~90% Top-5
            - **Input Size**: 224x224 pixels
            """)
    
    # Connect interactions
    classify_btn.click(
        fn=lambda img: (classify_image(img), analyze_confidence(img)),
        inputs=image_input,
        outputs=[classification_output, confidence_output]
    )
    
    # Auto-classify on image change
    image_input.change(
        fn=lambda img: (classify_image(img), analyze_confidence(img)),
        inputs=image_input,
        outputs=[classification_output, confidence_output]
    )

# Launch the demo
if __name__ == "__main__":
    demo.launch(
        share=True,  # Create public link
        debug=True,  # Enable detailed error messages
        server_name="0.0.0.0",  # Allow external access
        server_port=7860
    )
      `,
      language: 'python',
      category: 'tutorial'
    }
  ]
};

export const HUGGINGFACE_INTEGRATION_MODULE: AIModule = {
  id: 'huggingface-integration',
  title: 'Hugging Face Integration & Model Deployment',
  description: 'Integrate and deploy models using the Hugging Face ecosystem',
  duration: '4 hours',
  difficulty: 'intermediate',
  content: `
# Hugging Face Integration & Model Deployment

## Hugging Face Ecosystem Overview

Hugging Face provides a comprehensive ecosystem for machine learning, including model repositories, datasets, and deployment tools.

### Key Components
- **Model Hub**: Repository of pre-trained models
- **Datasets**: Curated dataset collection
- **Transformers**: State-of-the-art model library
- **Spaces**: Application hosting platform
- **Inference API**: Model serving infrastructure

## Working with Pre-trained Models

### Model Loading and Inference
Loading models from the Hub and making predictions.

### Fine-tuning Workflows
Adapting pre-trained models to specific tasks.

### Custom Model Upload
Sharing your own models with the community.

## Deployment Strategies

### Hugging Face Spaces
- Gradio and Streamlit hosting
- Docker container support
- GPU acceleration options
- Custom domain configuration

### Inference API
- Serverless model hosting
- Auto-scaling capabilities
- Cost-effective inference
- API key management

### Edge Deployment
- Model optimization for mobile
- ONNX conversion workflows
- Quantization techniques
- Performance monitoring

## Advanced Integration Patterns

### Multi-Model Pipelines
Combining multiple models for complex tasks.

### Real-time Inference
Building low-latency prediction services.

### Batch Processing
Efficient handling of large-scale inference workloads.

### Model Versioning
Managing model updates and rollbacks.
  `,
  objectives: [
    'Use Hugging Face models in applications',
    'Deploy models to Hugging Face Spaces',
    'Build custom inference pipelines',
    'Implement model versioning strategies'
  ]
};

export const NOCODE_AI_TOOLS_MODULE: AIModule = {
  id: 'nocode-ai-tools',
  title: 'No-Code AI Tools & Platforms',
  description: 'Leverage no-code platforms for rapid AI application development',
  duration: '3 hours',
  difficulty: 'beginner',
  content: `
# No-Code AI Tools & Platforms

## Introduction to No-Code AI

No-code AI platforms democratize artificial intelligence by enabling users to build AI applications without programming knowledge.

## Popular No-Code AI Platforms

### Zapier AI
- Workflow automation with AI
- Integration with thousands of apps
- Natural language processing
- Custom AI actions

### Microsoft Power Platform
- Power Apps for AI-powered applications
- Power Automate for AI workflows
- AI Builder for custom models
- Power BI for AI analytics

### Google AI Platform
- AutoML for custom models
- Vertex AI pipelines
- No-code model training
- Automated deployment

### Bubble.io with AI Plugins
- Visual web app development
- AI plugin integration
- Database and backend management
- User authentication and permissions

## Building AI Applications

### Chatbot Development
Creating conversational interfaces without coding.

### Document Processing
Automated data extraction and analysis.

### Image Recognition
Building computer vision applications.

### Predictive Analytics
Creating forecasting and recommendation systems.

## Integration Strategies

### API Connections
Connecting no-code platforms with AI services.

### Data Flow Management
Designing efficient data pipelines.

### User Experience Design
Creating intuitive AI-powered interfaces.

### Performance Optimization
Monitoring and improving application performance.

## Limitations and Considerations

### Customization Constraints
Understanding platform limitations.

### Vendor Lock-in
Planning for platform independence.

### Scalability Concerns
Preparing for growth and increased usage.

### Security Implications
Ensuring data protection and privacy.
  `,
  objectives: [
    'Build AI applications without coding',
    'Integrate multiple no-code platforms',
    'Design effective user experiences',
    'Understand platform limitations'
  ]
};

export const AI_API_INTEGRATION_MODULE: AIModule = {
  id: 'ai-api-integration',
  title: 'AI API Integration & Workflow Automation',
  description: 'Integrate AI services through APIs and automate complex workflows',
  duration: '3 hours',
  difficulty: 'intermediate',
  content: `
# AI API Integration & Workflow Automation

## AI API Landscape

Understanding the variety of AI services available through APIs and their capabilities.

### Major AI Service Providers
- OpenAI (GPT, DALL-E, Whisper)
- Google Cloud AI (Vision, Language, AutoML)
- Amazon Web Services (Rekognition, Comprehend, Textract)
- Microsoft Azure (Cognitive Services)
- Anthropic (Claude)

### Specialized AI APIs
- Computer vision services
- Natural language processing
- Speech recognition and synthesis
- Document analysis and OCR
- Recommendation engines

## API Integration Patterns

### Authentication and Security
- API key management
- OAuth workflows
- Rate limiting strategies
- Error handling patterns

### Request/Response Handling
- Asynchronous processing
- Batch operations
- Streaming responses
- Webhook integration

### Data Transformation
- Input preprocessing
- Output post-processing
- Format conversion
- Data validation

## Workflow Automation

### Sequential Processing
Creating linear AI processing pipelines.

### Parallel Processing
Running multiple AI services simultaneously.

### Conditional Logic
Implementing decision trees and branching logic.

### Human-in-the-Loop
Integrating human review and approval steps.

## Monitoring and Optimization

### Performance Metrics
- Latency tracking
- Accuracy monitoring
- Cost analysis
- Usage patterns

### Error Management
- Retry mechanisms
- Fallback strategies
- Error logging and alerting
- Recovery procedures

### Scaling Strategies
- Load balancing
- Auto-scaling policies
- Resource optimization
- Performance tuning
  `,
  objectives: [
    'Integrate multiple AI APIs effectively',
    'Build automated AI workflows',
    'Implement robust error handling',
    'Monitor and optimize performance'
  ]
};

export const VOICE_AI_DEVELOPMENT_MODULE: AIModule = {
  id: 'voice-ai-development',
  title: 'Voice AI & Speech Recognition Development',
  description: 'Build voice-enabled applications with speech recognition and synthesis',
  duration: '3 hours',
  difficulty: 'intermediate',
  content: `
# Voice AI & Speech Recognition Development

## Voice AI Fundamentals

Voice AI encompasses speech recognition, natural language understanding, and speech synthesis to create conversational interfaces.

### Key Technologies
- Automatic Speech Recognition (ASR)
- Text-to-Speech (TTS)
- Natural Language Understanding (NLU)
- Voice Activity Detection (VAD)
- Speaker Recognition

## Speech Recognition Implementation

### Browser-based Recognition
Using Web Speech API for client-side recognition.

### Cloud-based Solutions
Integrating with Google Speech-to-Text, AWS Transcribe, Azure Speech.

### Real-time Processing
Handling continuous speech streams and live transcription.

### Accuracy Optimization
- Language model customization
- Acoustic model adaptation
- Noise reduction techniques
- Post-processing improvements

## Speech Synthesis

### Neural TTS Systems
Modern approaches to natural-sounding speech synthesis.

### Voice Customization
- Voice cloning techniques
- Emotion and style control
- Multi-language support
- Prosody modification

### Real-time Synthesis
Low-latency speech generation for interactive applications.

## Voice Application Development

### Voice User Interface (VUI) Design
- Conversation flow design
- Error handling strategies
- User feedback mechanisms
- Accessibility considerations

### Multi-modal Interfaces
Combining voice with visual elements.

### Context Management
Maintaining conversation state and history.

### Integration Patterns
Embedding voice capabilities in existing applications.

## Production Deployment

### Performance Optimization
- Latency reduction techniques
- Bandwidth optimization
- Edge processing strategies
- Caching mechanisms

### Quality Assurance
- Voice recognition testing
- Synthesis quality evaluation
- User experience validation
- A/B testing frameworks
  `,
  objectives: [
    'Implement speech recognition systems',
    'Build text-to-speech applications',
    'Design effective voice user interfaces',
    'Deploy voice AI in production'
  ]
};